import { OverFlowIcon } from 'assests/GmsIcons';
import ToggleButton from 'Components/Common/components/Inputs/ToggleButton';
import OverlayIcon from 'Components/GMS/Rules/Components/OverlayIcon';
import React, { useState, useEffect, useRef } from 'react';
import { styles } from '../../../utils/styles';
import { TabConfig } from '../../../types/tab.types';

type PageConfigType = {
    id: string;
    title: string;
    autoAdvance: boolean;
};

type PageConfigProps = {
    id?: string;
    initialConfig?: Partial<{
        id: string;
        title: string;
    } & TabConfig>;
    onConfigChange?: (config: {
        id: string;
        title: string;
    } & TabConfig) => void;
}

export const PageConfig = ({ id, initialConfig, onConfigChange }: PageConfigProps) => {
    const baseId = id || 'page-config';
    const titleDebounceRef = useRef<NodeJS.Timeout>();

    const [localTitle, setLocalTitle] = useState<string>('');

    const defaultConfig: PageConfigType = {
        id: 'page-1',
        title: 'Page 1',
        autoAdvance: false,
    };

    const [config, setConfig] = useState<PageConfigType>({
        ...defaultConfig,
        ...initialConfig
    });

    useEffect(() => {
        setLocalTitle(config.title);
    }, [config.title]);

    useEffect(() => {
        if (initialConfig && initialConfig.id) {
            setConfig(prev => ({
                ...defaultConfig,
                ...initialConfig
            }));
        }
    }, [initialConfig]);

    const updateConfig = (updates: Partial<PageConfigType>) => {
        setConfig(prevConfig => {
            const newConfig = { ...prevConfig, ...updates };
            if (onConfigChange) {
                onConfigChange(newConfig);
            }
            return newConfig;
        });
    };

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newTitle = e.target.value;
        setLocalTitle(newTitle);
        
        if (titleDebounceRef.current) {
            clearTimeout(titleDebounceRef.current);
        }
        
        titleDebounceRef.current = setTimeout(() => {
            updateConfig({ title: newTitle });
        }, 500);
    };

    useEffect(() => {
        return () => {
            if (titleDebounceRef.current) {
                clearTimeout(titleDebounceRef.current);
            }
        };
    }, []);

    return (
        <div id={baseId} data-testid={baseId} className={styles.container}>
            <div id={`${baseId}-header`} className={`${styles.sectionTitle} tw-flex tw-justify-between tw-items-center`}>
                <h2 id={`${baseId}-title`} className="tw-text-sm tw-text-[#6D788C] tw-font-semibold">Page</h2>
                <button id={`${baseId}-menu-btn`} data-testid={`${baseId}-menu-btn`} className="tw-p-1 tw-text-gray-500">
                    <OverFlowIcon />
                </button>
            </div>

            <div id={`${baseId}-title-section`} className={styles.section}>
                <h3 id={`${baseId}-title-label`} className={styles.inputLabel}>Title</h3>
                <input
                    id={`${baseId}-title-input`}
                    data-testid={`${baseId}-title-input`}
                    type="text"
                    value={localTitle}
                    onChange={handleTitleChange}
                    className={styles.input}
                    placeholder="Enter page title"
                />
            </div>

            <div id={`${baseId}-auto-advance-section`} className={styles.section}>
                <div id={`${baseId}-auto-advance-header`} className="tw-flex tw-items-center tw-gap-2">
                    <h3 id={`${baseId}-auto-advance-label`} className={styles.inputLabel}>Auto Advance</h3>
                    <OverlayIcon message={'When enabled, the UI will automatically proceed to the next page after all required fields are completed.'} />
                </div>
                <ToggleButton
                    id={`${baseId}-auto-advance-toggle`}
                    name="auto-advance"
                    value={config.autoAdvance}
                    onChange={(e) => {
                        updateConfig({ autoAdvance: e.target.checked })}
                    }
                />
            </div>
        </div>
    );
};